import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Icon } from '@shopify/polaris';
import { CancelSmallMinor } from '@shopify/polaris-icons';
import { getWithExpiry, setWithExpiry } from '../../utils/LocalStorageExpiry';
import { PLANS, RECOMMENDED, formatDollars, formatPrice, getPlanOffer } from '../../pages/SubscriptionPage/plans';
import './HalloweenOfferBanner.css';

// The Early Bird BFCM Offer (the Halloween Offer until Nov 2): stores on the free plan pay $1 / $2 / $3 for the
// first month of Lite / Professional / Business. The backend decides who gets it and sends the terms in the
// install response, so the banner is there on the dashboard's first render instead of shifting the page when it
// pops in; the banner only adds the deadline. The offer ends at one moment everywhere: the last second of Sunday Nov 8 in GMT+1. The backend's
// EndsAt is the second after it, which is already Nov 9 there, so the day count below can't use that.
export const OFFER_ENDS = new Date('2026-11-08T22:59:59Z');
const DISMISSED_KEY = 'dismissed_earlyBirdBfcmOffer2026';
const DISMISS_FOR = 7 * 24 * 60 * 60 * 1000;
const DAY = 24 * 60 * 60 * 1000;

// True while the merchant sees the offer: the backend offers it to the store, and it hasn't ended. The
// Early Bird BFCM Deal's banner stands aside for it until then.
export const showsHalloweenOffer = (meta, now) => !!meta?.HalloweenOffer && now <= OFFER_ENDS;

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

// Counted in calendar days, so the last day says "today" rather than "1 day left".
export const deadlineText = (now) => {
  const days = Math.round((startOfDay(OFFER_ENDS) - startOfDay(now)) / DAY);
  return days === 0 ? 'Ends today' : days === 1 ? 'Ends tomorrow' : `Ends in ${days} days`;
};

// "$1, $2 or $3"
const priceList = (offers) => {
  const prices = offers.map((offer) => formatDollars(offer.promo.price));
  return prices.length > 1 ? `${prices.slice(0, -1).join(', ')} or ${prices[prices.length - 1]}` : prices[0];
};

// "Business is $3 for the first month instead of $25.99, after your 3-day free trial."
const priceLine = ({ name, price, promo, trialDays }) =>
  `${name} is ${formatDollars(promo.price)} for the first month instead of ${formatPrice(price)}${
    trialDays ? `, after your ${trialDays}-day free trial` : ''
  }.`;

const HalloweenOfferBanner = ({ meta, now = new Date() }) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => !!getWithExpiry(DISMISSED_KEY));
  if (dismissed || !showsHalloweenOffer(meta, now)) return null;

  const ctx = { currentPlan: meta.SubscriptionPlan, trialUsedForPlans: meta.TrialUsedForPlans, halloweenOffer: meta.HalloweenOffer };
  const ladder = PLANS.map((plan) => getPlanOffer(plan, ctx)).filter((offer) => offer.promo?.halloween);
  if (!ladder.length) return null;
  const pitched = ladder.find((offer) => offer.plan === RECOMMENDED.plan) || ladder[ladder.length - 1];

  const onDismiss = () => {
    setDismissed(true);
    setWithExpiry(DISMISSED_KEY, 'true', DISMISS_FOR);
  };

  return (
    <section className="halloween" aria-label="Early Bird BFCM offer">
      <ol className="halloween-ladder" aria-label="First month">
        {ladder.map((offer) => (
          <li key={offer.plan} className={offer.plan === pitched.plan ? 'is-highlighted' : undefined}>
            <span className="halloween-ladder__plan">{offer.name}</span>
            <span className="halloween-ladder__price">{formatDollars(offer.promo.price)}</span>
          </li>
        ))}
      </ol>
      <div className="halloween__copy">
        <span className="halloween__kicker">Early Bird BFCM offer</span>
        <h2 className="halloween__title">{`Your first month for ${priceList(ladder)}`}</h2>
        <p className="halloween__body">
          {`Get your BFCM sales ready before the rush. ${priceLine(pitched)}`}
        </p>
      </div>
      <div className="halloween__cta">
        <Button size="large" onClick={() => navigate('/subscription')}>Pick a plan</Button>
        <span className="halloween__deadline">{deadlineText(now)}</span>
      </div>
      <button type="button" className="halloween__dismiss" onClick={onDismiss} aria-label="Dismiss Early Bird BFCM offer">
        <Icon source={CancelSmallMinor} tone="inherit" />
      </button>
    </section>
  );
};

export default HalloweenOfferBanner;
